import { Component, OnInit } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { BillingFree } from '@classes/billing-free';
import { BillingFreeItem } from '@classes/billing-free-item';
import { MessageBoxParameter } from '@classes/message-box-parameter';
import { MessageBox } from '@components/message-box/message-box.component';
import { BillingType } from '@enums/billing-type.enum';
import { TranslateService } from '@ngx-translate/core';
import { AutocompleteObjectValidator } from '@shared/validators/autocomplete-object-validator';
import { BillingFreeService } from 'app/core/services/billing-free.service';
import { UiService } from 'app/core/services/ui.service';
import { BillingFreeItemComponent } from '../billing-free-item/billing-free-item.component';

@Component({
  selector: 'app-billing-free',
  templateUrl: './billing-free.component.html',
  styleUrls: ['./billing-free.component.scss'],
  styles: [`
  >>> app-billing-free {
    display: flex;
    height: calc(100% - 90px);
    overflow: auto;
    }
`]
})
export class BillingFreeComponent implements OnInit {

  billingFreeForm: FormGroup;
  dataSource = new MatTableDataSource<BillingFreeItem>();
  selectedRowID: string;
  displayedColumns: string[] = [
    'name',
    'value',
    'tva',
    'priceHtva',
    'priceTva',
    'totalHtva',
    'totalTva',
    'remove'
  ];

  constructor(
    private formBuilder: FormBuilder,
    private billingFreeService: BillingFreeService,
    public uiService: UiService,
    private translateService: TranslateService,
    private autocompleteObjectValidator: AutocompleteObjectValidator,
    public dialog: MatDialog,
  ) { }

  ngOnInit() {
    this.initFormBuilder();
  }
  private initFormBuilder() {
    const fb = this.formBuilder;
    this.billingFreeForm = fb.group({
      billingTypeId: fb.control(null, [Validators.required]),
      site: fb.control(null, [this.autocompleteObjectValidator.autocompleteObjectValidator()]),
      company: fb.control(null, [Validators.required, this.autocompleteObjectValidator.autocompleteObjectValidator()]),
      date: fb.control(null, [Validators.required]),
      comment: fb.control(null, []),
      tva: fb.control({ value: 21, disabled: false }, [Validators.required, Validators.min(0), Validators.max(100)]),
    });

    this.billingFreeForm.controls.tva.valueChanges.subscribe(tva => {
      this.dataSource.data.forEach(fe => {
        const quantity = fe.value;
        const priceHtva = fe.priceHtva;
        fe.tva = tva;
        fe.priceTva = priceHtva + (tva / 100 * priceHtva);
        fe.totalHtva = priceHtva * quantity;
        fe.totalTva = (priceHtva + (tva / 100 * priceHtva)) * quantity;
      });
    });
    this.billingFreeForm.controls.billingTypeId.valueChanges.subscribe(billingTypeId => {
      this.dataSource.data.forEach(fe => {
        fe.priceHtva = Math.abs(fe.priceHtva);
        fe.priceTva = Math.abs(fe.priceTva);
        fe.totalHtva = Math.abs(fe.totalHtva);
        fe.totalTva = Math.abs(fe.totalTva);
        if (billingTypeId != "4")//BillingType.Free
        {
          fe.priceHtva = fe.priceHtva * -1;
          fe.priceTva = fe.priceTva * -1;
          fe.totalHtva = fe.totalHtva * -1;
          fe.totalTva = fe.totalTva * -1;
        }
      });
    });

  }

  onAddItem() {
    const data = new BillingFreeItem();
    data.tva = this.billingFreeForm.controls.tva.value;
    data.billingType = this.billingFreeForm.controls.billingTypeId.value == "4" ? BillingType.Free : BillingType.CreditNote
    const dialogRef = this.dialog.open(BillingFreeItemComponent, {
      data,
      hasBackdrop: true
    });
    dialogRef.afterClosed().subscribe(data => {
      if (data && data.add) {
        data.value.id = (this.dataSource?.data || []).length > 0 ? (Math.max(...(this.dataSource?.data || []).map(m => m.id)) + 1) : 1;
        // data.value.totalHtva = data.value.value * data.value.priceHtva;
        // data.value.totalTva = data.value.value * data.value.priceTva;
        const dataSource = this.dataSource.data;
        dataSource.push(data.value);
        this.dataSource.data = dataSource;
      }
    });
  }
  onRemoveItem(element: BillingFreeItem) {
    if (element.id > -1) {

      const messageBoxParameter: MessageBoxParameter = {
        message: this.translateService.instant('billing.billing-free-remove-item',
          {
            name: element.name,
            quantity: element.value,
          }),
        dialog: this.dialog,
        title: 'Confirmation',
        buttons: ['yes', 'no'],
        width: "400px"
      };

      MessageBox.show(messageBoxParameter).subscribe(mbResult => {
        if (mbResult && mbResult.value) {
          this.dataSource.data = [...this.dataSource.data.filter(f => f.id != element.id)]; // new ref!    
        }
      });

    }
  }

  onSubmit() {

    const totalHtva = this.dataSource.data.map(m => m.totalHtva).reduce((a, b) => (a || 0) + (b || 0), 0)?.toLocaleString(
      undefined, // leave undefined to use the visitor's browser 
      // locale or a string like 'en-US' to override it.
      { minimumFractionDigits: 2 }
    )

    const message = this.translateService.instant('billing.billing-free-confirm-create',
      {
        billingType: this.billingFreeForm.controls.billingTypeId.value == "4" ? "facture libre" : "note de crédit",
        totalHtva: totalHtva.toLocaleString().replace(/,/g, " ",).replace(".", ","),
        companyName: this.billingFreeForm.controls.company.value.name
      }
    );

    const messageBoxParameter: MessageBoxParameter = {
      message,
      dialog: this.dialog,
      title: 'Confirmation',
      buttons: ['yes', 'no'],
      width: "400px"
    };

    MessageBox.show(messageBoxParameter).subscribe(mbResult => {
      if (mbResult && mbResult.value) {
        const billingFree = new BillingFree(
          this.billingFreeForm.value
        );
        billingFree.billingFreeItems = this.dataSource.data;
        this.uiService.addRouteLoading();
        this.billingFreeService.createOrUpdateBillingFree(billingFree).subscribe(data => {
          this.initFormBuilder();
          this.dataSource.data = [];
          this.uiService.removeRouteLoading();
          this.uiService.displayMessageToast('billing.billing-free-success-create');
        }, error => {
          console.error(error);
          this.uiService.removeRouteLoading();
          this.uiService.displayMessageToast('errormessage.common.unknow');
        });
      }
    });



  }


  isRequiredField(field: string) {
    const form_field = <AbstractControl>this.billingFreeForm.get(field);
    if (!form_field.validator) {
      return false;
    }

    const validator = form_field.validator({} as AbstractControl);
    return (validator && validator.required);
  }

}
